import React, { useState, useEffect, useContext } from 'react';
import { UserContext, CompanyContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Receipt, 
  User, 
  Building2, 
  Calculator,
  Download,
  CheckCircle,
  AlertCircle,
  QrCode,
  X
} from 'lucide-react';

const InvoiceGeneratorFixed = ({ isOpen, onClose, saleData }) => {
  const { user, API } = useContext(UserContext);
  const { company } = useContext(CompanyContext);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Client info
  const [clientType, setClientType] = useState('B2C');
  const [clientInfo, setClientInfo] = useState({
    last_name: '',
    company_name: '',
    ncc_number: '',
    tax_regime: 'TEE',
    email: '',
    phone: '',
    address: ''
  });
  
  // Invoice details
  const [invoiceDetails, setInvoiceDetails] = useState({
    payment_method: 'Espèce',
    apply_vat: true,  // Case à cocher TVA
    vat_rate: 18,
    apply_asdi: true,  // Case à cocher ASDI
    asdi_rate: 1.5
  });
  
  const [generatedInvoice, setGeneratedInvoice] = useState(null);

  const generateInvoice = async () => {
    try {
      console.log('Starting invoice generation...');
      console.log('Company:', company);
      console.log('Sale data:', saleData);
      
      if (!company) {
        toast.error('❌ Informations entreprise manquantes');
        return;
      }

      if (!saleData || !saleData.products || saleData.products.length === 0) {
        toast.error('❌ Aucun produit à facturer');
        return;
      }

      setLoading(true);

      // Données simplifiées pour éviter les erreurs
      const invoiceData = {
        customer_info: clientType === 'B2B' ? {
          is_company: true,
          company_name: clientInfo.company_name,
          ncc_number: clientInfo.ncc_number,
          tax_regime: clientInfo.tax_regime,
          email: clientInfo.email
        } : {
          is_company: false,
          customer_name: clientInfo.last_name,
          email: clientInfo.email,
          phone: clientInfo.phone,
          address: clientInfo.address
        },
        
        line_items: saleData.products.map(product => {
          // Le prix produit (ex: 150F) est le prix TTC affiché en magasin = ce que paie le client.
          // On dérive le HT seulement si la TVA est appliquée, sinon HT = TTC.
          const priceTTC = product.price;
          const vatRate = invoiceDetails.apply_vat ? invoiceDetails.vat_rate / 100 : 0;
          const unitPriceHT = invoiceDetails.apply_vat
            ? Math.round((priceTTC / (1 + vatRate)) * 100) / 100
            : priceTTC;
          return {
            reference_code: `REF-${product.name.replace(/\s+/g, '_').toUpperCase()}`,
            designation: product.name,
            quantity: product.quantity,
            unit_price_ht: unitPriceHT,
            unit_price_ttc: priceTTC,  // prix unitaire affiché = prix TTC payé par le client
            unit_of_measure: 'pièce',
            vat_rate: vatRate,
            asdi_rate: invoiceDetails.apply_asdi ? invoiceDetails.asdi_rate / 100 : 0,
            discount_rate: 0,
            apply_vat: invoiceDetails.apply_vat,
            apply_asdi: invoiceDetails.apply_asdi
          };
        }),
        
        payment_method: invoiceDetails.payment_method,
        linked_sale_ids: saleData.linked_sale_ids || [],
        user_id: user.id
      };

      console.log('Invoice data prepared:', invoiceData);

      const response = await axios.post(`${API}/invoices/generate`, invoiceData);
      console.log('Invoice generated:', response.data);
      
      setGeneratedInvoice(response.data);
      setStep(3);
      
      toast.success('✅ Facture générée avec succès !');
      
    } catch (error) {
      console.error('Error generating invoice:', error);
      
      if (error.response?.status === 400 && error.response.data.detail.includes('Entreprise non trouvée')) {
        toast.error('🏢 Entreprise non trouvée. Veuillez créer votre entreprise d\'abord.');
        
        const createCompany = window.confirm('Créer votre entreprise maintenant ?');
        if (createCompany) {
          window.open('/company/create', '_blank');
        }
      } else {
        toast.error(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoiceTicket = () => {
    if (!generatedInvoice) return;

    try {
      const customer = generatedInvoice.customer_info?.is_company ? 
        generatedInvoice.customer_info.company_name :
        generatedInvoice.customer_info?.customer_name || 'Client';

      // Template de ticket de caisse
      const ticketHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Ticket ${generatedInvoice.invoice_number}</title>
  <style>
    body { 
      font-family: 'Courier New', monospace; 
      margin: 0; 
      padding: 15px;
      background: white;
      color: #000;
      font-size: 12px;
      line-height: 1.3;
      max-width: 300px;
      margin: 0 auto;
    }
    
    .ticket {
      border: 2px dashed #333;
      padding: 15px;
      background: #fefefe;
    }
    
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .large { font-size: 16px; }
    .small { font-size: 10px; }
    
    .header {
      text-align: center;
      border-bottom: 1px solid #333;
      padding-bottom: 10px;
      margin-bottom: 10px;
    }
    
    .company-name {
      font-size: 18px;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 5px;
    }
    
    .separator {
      border-top: 1px dashed #666;
      margin: 10px 0;
      height: 1px;
    }
    
    .double-separator {
      border-top: 2px solid #333;
      margin: 15px 0;
      height: 2px;
    }
    
    .item-line {
      display: flex;
      justify-content: space-between;
      margin: 3px 0;
      padding: 2px 0;
    }
    
    .item-name { 
      flex: 1; 
      text-align: left;
      font-weight: bold;
    }
    
    .item-qty-price {
      text-align: right;
      min-width: 80px;
    }
    
    .total-line {
      display: flex;
      justify-content: space-between;
      margin: 5px 0;
      font-weight: bold;
    }
    
    .final-total {
      font-size: 16px;
      font-weight: bold;
      border-top: 2px solid #333;
      border-bottom: 2px solid #333;
      padding: 8px 0;
      margin: 10px 0;
    }
    
    .footer {
      text-align: center;
      font-size: 9px;
      color: #666;
      margin-top: 15px;
      border-top: 1px solid #ccc;
      padding-top: 10px;
    }
    
    @media print {
      body { margin: 0; padding: 0; }
      .ticket { border: none; margin: 0; }
    }
  </style>
</head>
<body>
  <div class="ticket">
    
    <!-- Header du magasin -->
    <div class="header">
      <div class="company-name">${generatedInvoice.issuer_company_name}</div>
      <div class="small">Boulangerie Artisanale</div>
      <div class="small">NCC: ${generatedInvoice.issuer_ncc}</div>
      <div class="small">Tél: ${generatedInvoice.issuer_phone}</div>
      ${generatedInvoice.issuer_email ? `<div class="small">${generatedInvoice.issuer_email}</div>` : ''}
    </div>
    
    <!-- Informations ticket -->
    <div class="center" style="margin: 15px 0;">
      <div class="bold large">TICKET DE CAISSE</div>
      <div class="bold">N° ${generatedInvoice.invoice_number}</div>
      <div>${new Date(generatedInvoice.sale_date).toLocaleDateString('fr-FR')} - ${generatedInvoice.sale_time}</div>
      <div class="small">Vendeur: ${generatedInvoice.seller_name}</div>
    </div>
    
    <div class="separator"></div>
    
    <!-- Client -->
    <div style="margin: 10px 0;">
      <div class="bold">CLIENT:</div>
      <div>${customer}</div>
      ${generatedInvoice.customer_info?.phone ? `<div class="small">Tél: ${generatedInvoice.customer_info.phone}</div>` : ''}
    </div>
    
    <div class="separator"></div>
    
    <!-- Articles -->
    <div class="bold center" style="margin: 10px 0;">ARTICLES VENDUS</div>
    
    ${generatedInvoice.line_items.map((item, index) => {
      const totalHT = item.quantity * item.unit_price_ht;
      const vatAmount = item.apply_vat ? totalHT * item.vat_rate : 0;
      const totalTTC = totalHT + vatAmount;
      // Prix unitaire TTC affiché = ce que paie le client en caisse (ex: 150F)
      const unitPriceDisplayed = item.unit_price_ttc != null
        ? item.unit_price_ttc
        : (item.apply_vat ? item.unit_price_ht * (1 + item.vat_rate) : item.unit_price_ht);
      
      return `
        <div class="item-line">
          <span class="item-name">${item.designation}</span>
        </div>
        <div class="item-line small">
          <span>  ${item.quantity} ${item.unit_of_measure} × ${Math.round(unitPriceDisplayed)} F</span>
          <span class="bold">${Math.round(totalTTC)} F</span>
        </div>
        ${item.apply_vat ? `<div class="item-line small">  (dont TVA ${Math.round(item.vat_rate * 100)}%)</div>` : '<div class="item-line small">  TVA exonérée</div>'}
      `;
    }).join('')}
    
    <div class="separator"></div>
    
    <!-- Totaux -->
    <div class="total-line">
      <span>SOUS-TOTAL HT:</span>
      <span>${Math.round(generatedInvoice.subtotal_ht)} F</span>
    </div>
    
    ${generatedInvoice.line_items.some(item => item.apply_vat) ? `
      <div class="total-line">
        <span>TVA:</span>
        <span>${Math.round(generatedInvoice.total_vat)} F</span>
      </div>
    ` : `
      <div class="total-line small" style="color: #666;">
        <span>TVA:</span>
        <span>EXONÉRÉE</span>
      </div>
    `}
    
    <div class="total-line">
      <span>TOTAL TTC:</span>
      <span>${Math.round(generatedInvoice.total_ttc)} F</span>
    </div>
    
    ${generatedInvoice.line_items.some(item => item.apply_asdi) ? `
      <div class="total-line">
        <span>ASDI:</span>
        <span>${Math.round(generatedInvoice.total_asdi)} F</span>
      </div>
    ` : `
      <div class="total-line small" style="color: #666;">
        <span>ASDI:</span>
        <span>NON APPLICABLE</span>
      </div>
    `}
    
    <div class="double-separator"></div>
    
    <!-- Total final -->
    <div class="final-total center">
      <div>TOTAL À PAYER</div>
      <div class="large">${Math.round(generatedInvoice.total_to_pay)} F CFA</div>
    </div>
    
    <div class="double-separator"></div>
    
    <!-- Mode de paiement -->
    <div class="center bold" style="margin: 10px 0;">
      PAIEMENT: ${generatedInvoice.payment_method.toUpperCase()}
    </div>
    
    <div class="separator"></div>
    
    <!-- QR Code (textuel) -->
    <div class="center" style="margin: 15px 0;">
      <div class="bold">VERIFICATION</div>
      <div class="small">Scannez ce code:</div>
      <div style="border: 1px solid #333; padding: 8px; margin: 5px 0; font-family: monospace; word-break: break-all;">
        ${generatedInvoice.invoice_number}
      </div>
      <div class="small">maboulangerieenpoche.com</div>
    </div>
    
    <!-- Footer légal -->
    <div class="footer">
      <div class="separator"></div>
      <div style="margin-top: 10px;">
        <div class="bold">MERCI DE VOTRE VISITE</div>
        <div>À BIENTÔT !</div>
      </div>
      
      <div style="margin-top: 15px; font-size: 8px;">
        <div>Ticket généré le:</div>
        <div>${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}</div>
      </div>
      
      <div style="margin-top: 10px; font-size: 8px; border-top: 1px solid #ccc; padding-top: 8px;">
        <div>© 2025 ${generatedInvoice.issuer_company_name}</div>
        <div>Créé par Elouho Ettien Ane Martial T.</div>
        <div>Consultant Juriste-Fiscaliste</div>
        <div>+225 07 69 54 54 69</div>
      </div>
    </div>
    
    <!-- Boutons pour impression -->
    <div class="center" style="margin-top: 20px; border-top: 2px solid #333; padding-top: 15px;">
      <button onclick="window.print()" style="
        padding: 8px 16px; 
        background: #333; 
        color: white; 
        border: none; 
        border-radius: 4px; 
        font-weight: bold; 
        cursor: pointer;
        margin-right: 10px;
      ">
        🖨️ IMPRIMER
      </button>
      <button onclick="window.close()" style="
        padding: 8px 16px; 
        background: #666; 
        color: white; 
        border: none; 
        border-radius: 4px; 
        font-weight: bold; 
        cursor: pointer;
      ">
        FERMER
      </button>
    </div>
  </div>
</body>
</html>`;

      // Créer et télécharger le ticket
      const blob = new Blob([ticketHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ticket_${generatedInvoice.invoice_number}_${customer.replace(/\s+/g, '_')}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('🎫 Ticket de caisse téléchargé avec succès !');
      
      // Ouvrir aussi dans une nouvelle fenêtre
      const printWindow = window.open('', '_blank', 'width=350,height=600');
      if (printWindow) {
        printWindow.document.write(ticketHTML);
        printWindow.document.close();
      }
      
    } catch (error) {
      console.error('Error downloading ticket:', error);
      toast.error('❌ Erreur lors du téléchargement du ticket');
    }
  };


  const downloadInvoiceJSON = () => {
    if (!generatedInvoice) return;

    try {
      // Télécharger aussi la version JSON pour archivage
      const jsonData = {
        ...generatedInvoice,
        download_info: {
          downloaded_at: new Date().toISOString(),
          downloaded_by: user?.username || 'Utilisateur',
          download_format: 'JSON'
        }
      };

      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_${generatedInvoice.invoice_number}_data.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('📊 Données de facture téléchargées !');
      
    } catch (error) {
      console.error('Error downloading JSON:', error);
      toast.error('❌ Erreur lors du téléchargement JSON');
    }
  };

  const resetForm = () => {
    setStep(1);
    setClientInfo({
      last_name: '',
      company_name: '',
      ncc_number: '',
      tax_regime: 'TEE',
      email: '',
      phone: '',
      address: ''
    });
    setGeneratedInvoice(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        resetForm();
      }
      onClose(open);
    }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="h-6 w-6 text-green-600" />
              Génération de Facture - Étape {step}/3
            </div>
            <Button
              onClick={() => {
                resetForm();
                onClose();
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Étape 1: Client */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>📝 Informations du Client</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Type de client */}
                <div className="flex gap-4">
                  <button
                    onClick={() => setClientType('B2C')}
                    className={`flex-1 p-4 border-2 rounded-lg ${
                      clientType === 'B2C' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    👤 Particulier (B2C)
                  </button>
                  <button
                    onClick={() => setClientType('B2B')}
                    className={`flex-1 p-4 border-2 rounded-lg ${
                      clientType === 'B2B' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    🏢 Entreprise (B2B)
                  </button>
                </div>

                {clientType === 'B2C' ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Nom du client *</Label>
                      <Input
                        value={clientInfo.last_name}
                        onChange={(e) => setClientInfo(prev => ({...prev, last_name: e.target.value}))}
                        placeholder="Ex: Jean Dupont"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={clientInfo.email}
                          onChange={(e) => setClientInfo(prev => ({...prev, email: e.target.value}))}
                          placeholder="client@email.com"
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={clientInfo.phone}
                          onChange={(e) => setClientInfo(prev => ({...prev, phone: e.target.value}))}
                          placeholder="+225 XX XX XX XX XX"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Adresse *</Label>
                      <Textarea
                        value={clientInfo.address}
                        onChange={(e) => setClientInfo(prev => ({...prev, address: e.target.value}))}
                        placeholder="Adresse complète du client"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Nom de la société *</Label>
                      <Input
                        value={clientInfo.company_name}
                        onChange={(e) => setClientInfo(prev => ({...prev, company_name: e.target.value}))}
                        placeholder="Ex: SARL Client"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>NCC Client *</Label>
                        <Input
                          value={clientInfo.ncc_number}
                          onChange={(e) => setClientInfo(prev => ({...prev, ncc_number: e.target.value}))}
                          placeholder="1234567890123"
                          required
                        />
                      </div>
                      <div>
                        <Label>Régime d'imposition</Label>
                        <select
                          value={clientInfo.tax_regime}
                          onChange={(e) => setClientInfo(prev => ({...prev, tax_regime: e.target.value}))}
                          className="w-full h-10 px-3 border rounded-lg"
                        >
                          <option value="TEE">TEE</option>
                          <option value="RME">RME</option>
                          <option value="RSI">RSI</option>
                          <option value="RNI">RNI</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={clientInfo.email}
                        onChange={(e) => setClientInfo(prev => ({...prev, email: e.target.value}))}
                        placeholder="entreprise@email.com"
                      />
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => setStep(2)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={
                    clientType === 'B2B' ? 
                      !clientInfo.company_name || !clientInfo.ncc_number : 
                      !clientInfo.last_name || !clientInfo.address
                  }
                >
                  Étape Suivante →
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Étape 2: Détails */}
          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>⚙️ Paramètres de Facturation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Info entreprise */}
                <div className={`p-4 border rounded-lg ${company ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  {company ? (
                    <div>
                      <p className="font-bold text-green-800">✅ Entreprise: {company.raison_sociale}</p>
                      <p className="text-sm text-green-700">NCC: {company.numero_ncc}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-bold text-red-800">❌ Entreprise manquante</p>
                      <Button size="sm" onClick={() => window.open('/company/create', '_blank')}>
                        Créer Entreprise
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Mode de paiement */}
                  <div className="space-y-2">
                    <Label className="text-sm font-bold">💳 Mode de paiement</Label>
                    <select
                      value={invoiceDetails.payment_method}
                      onChange={(e) => setInvoiceDetails(prev => ({...prev, payment_method: e.target.value}))}
                      className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg bg-white focus:border-blue-500"
                    >
                      <option value="Espèce">💵 Espèce</option>
                      <option value="Virement">🏦 Virement bancaire</option>
                      <option value="Chèque">📝 Chèque</option>
                      <option value="Carte">💳 Carte bancaire</option>
                      <option value="Mobile">📱 Mobile Money</option>
                    </select>
                  </div>

                  {/* TVA avec case à cocher */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="apply_vat"
                        checked={invoiceDetails.apply_vat}
                        onChange={(e) => setInvoiceDetails(prev => ({...prev, apply_vat: e.target.checked}))}
                        className="h-5 w-5 text-blue-600 border-2 border-blue-300 rounded focus:ring-blue-500"
                      />
                      <Label htmlFor="apply_vat" className="text-base font-bold text-blue-800 cursor-pointer">
                        📊 Appliquer la TVA (Taxe sur la Valeur Ajoutée)
                      </Label>
                    </div>
                    
                    {invoiceDetails.apply_vat && (
                      <div className="ml-8 space-y-2">
                        <Label className="text-sm font-medium text-blue-700">Taux TVA (%)</Label>
                        <div className="flex gap-3">
                          {[18, 20, 15, 10].map(rate => (
                            <button
                              key={rate}
                              onClick={() => setInvoiceDetails(prev => ({...prev, vat_rate: rate}))}
                              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                                invoiceDetails.vat_rate === rate 
                                  ? 'border-blue-500 bg-blue-100 text-blue-800' 
                                  : 'border-gray-300 hover:border-blue-400'
                              }`}
                            >
                              {rate}%
                            </button>
                          ))}
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={invoiceDetails.vat_rate}
                            onChange={(e) => setInvoiceDetails(prev => ({...prev, vat_rate: parseFloat(e.target.value) || 0}))}
                            className="w-20 h-10 text-center"
                            placeholder="Autre"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ASDI avec case à cocher */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="apply_asdi"
                        checked={invoiceDetails.apply_asdi}
                        onChange={(e) => setInvoiceDetails(prev => ({...prev, apply_asdi: e.target.checked}))}
                        className="h-5 w-5 text-orange-600 border-2 border-orange-300 rounded focus:ring-orange-500"
                      />
                      <Label htmlFor="apply_asdi" className="text-base font-bold text-orange-800 cursor-pointer">
                        📋 Appliquer l'ASDI (Autre Taxe Spéciale)
                      </Label>
                    </div>
                    
                    {invoiceDetails.apply_asdi && (
                      <div className="ml-8 space-y-2">
                        <Label className="text-sm font-medium text-orange-700">Taux ASDI (%)</Label>
                        <div className="flex gap-3">
                          {[1, 1.5, 2, 5].map(rate => (
                            <button
                              key={rate}
                              onClick={() => setInvoiceDetails(prev => ({...prev, asdi_rate: rate}))}
                              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                                invoiceDetails.asdi_rate === rate 
                                  ? 'border-orange-500 bg-orange-100 text-orange-800' 
                                  : 'border-gray-300 hover:border-orange-400'
                              }`}
                            >
                              {rate}%
                            </button>
                          ))}
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="10"
                            value={invoiceDetails.asdi_rate}
                            onChange={(e) => setInvoiceDetails(prev => ({...prev, asdi_rate: parseFloat(e.target.value) || 0}))}
                            className="w-20 h-10 text-center"
                            placeholder="Autre"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Aperçu des calculs */}
                  {saleData && saleData.products && (
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-gray-800 mb-3">🧮 Aperçu des Calculs</h4>
                      {saleData.products.map((product, index) => {
                        // Le prix produit (ex: 150F) est le prix TTC affiché en magasin = ce que paie le client
                        const priceTTC = product.price;
                        const vatRate = invoiceDetails.apply_vat ? invoiceDetails.vat_rate / 100 : 0;
                        const priceHT = invoiceDetails.apply_vat
                          ? Math.round((priceTTC / (1 + vatRate)) * 100) / 100
                          : priceTTC;
                        const totalTTC = priceTTC * product.quantity;
                        const totalHT = priceHT * product.quantity;
                        const vatAmount = totalTTC - totalHT;
                        const asdiAmount = invoiceDetails.apply_asdi ? totalTTC * (invoiceDetails.asdi_rate / 100) : 0;
                        const finalTotal = totalTTC + asdiAmount;

                        return (
                          <div key={index} className="text-sm space-y-1 mb-3 p-3 bg-white rounded border">
                            <div className="font-bold text-blue-800">{product.name} (x{product.quantity})</div>
                            <div>Prix unitaire (TTC payé): {Math.round(priceTTC)} F CFA</div>
                            <div>{product.quantity} × {Math.round(priceTTC)} F = {Math.round(totalTTC)} F CFA</div>
                            {invoiceDetails.apply_vat && (
                              <div className="text-blue-600 text-xs">(dont HT: {Math.round(totalHT)} F + TVA {invoiceDetails.vat_rate}%: {Math.round(vatAmount)} F)</div>
                            )}
                            {!invoiceDetails.apply_vat && (
                              <div className="text-gray-500 text-xs">TVA exonérée</div>
                            )}
                            {invoiceDetails.apply_asdi && (
                              <div className="text-orange-600">+ ASDI ({invoiceDetails.asdi_rate}%): {Math.round(asdiAmount)} F CFA</div>
                            )}
                            <div className="font-bold text-green-600 border-t pt-1">
                              💰 TOTAL: {Math.round(finalTotal)} F CFA
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                    ← Retour
                  </Button>
                  <Button
                    onClick={generateInvoice}
                    disabled={loading || !company}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Génération...' : 'Générer Facture'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Étape 3: Facture générée */}
          {step === 3 && generatedInvoice && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  ✅ Facture Générée
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-bold text-green-800 mb-2">✅ Facture créée avec succès !</h3>
                  <div className="space-y-1 text-sm text-green-700">
                    <p><strong>N° Facture:</strong> {generatedInvoice.invoice_number}</p>
                    <p><strong>Client:</strong> {
                      generatedInvoice.customer_info?.is_company ? 
                        generatedInvoice.customer_info.company_name :
                        generatedInvoice.customer_info?.customer_name || 'Client'
                    }</p>
                    <p><strong>Total:</strong> {Math.round(generatedInvoice.total_to_pay)} F CFA</p>
                    <p><strong>Date:</strong> {new Date().toLocaleDateString('fr-FR')}</p>
                    <p><strong>Produits:</strong> {generatedInvoice.line_items.length} article(s)</p>
                  </div>
                </div>

                {/* Aperçu des produits */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 font-bold text-sm">
                    📦 Produits Facturés
                  </div>
                  <div className="p-4 space-y-2">
                    {generatedInvoice.line_items.map((item, index) => {
                      const unitPriceDisplayed = item.unit_price_ttc != null
                        ? item.unit_price_ttc
                        : (item.apply_vat ? item.unit_price_ht * (1 + item.vat_rate) : item.unit_price_ht);
                      const lineTotal = item.quantity * unitPriceDisplayed;
                      return (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                          <div>
                            <span className="font-medium">{item.designation}</span>
                            <span className="text-xs text-gray-500 ml-2">({item.reference_code})</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{item.quantity} × {Math.round(unitPriceDisplayed)} F = {Math.round(lineTotal)} F</div>
                            <div className="text-xs text-gray-500">
                              {item.unit_of_measure} • 
                              {item.apply_vat ? ` TVA ${Math.round(item.vat_rate * 100)}% incluse` : ' TVA Exonérée'} • 
                              {item.apply_asdi ? ` ASDI ${Math.round(item.asdi_rate * 100)}%` : ' ASDI N/A'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    onClick={downloadInvoiceTicket}
                    className="bg-green-600 hover:bg-green-700 text-white shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    🎫 Ticket de Caisse
                  </Button>
                  
                  <Button
                    onClick={downloadInvoiceJSON}
                    className="bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    📊 Données JSON
                  </Button>
                  
                  <Button
                    onClick={() => {
                      const qrLink = `https://maboulangerieenpoche.com/invoice/${generatedInvoice.invoice_number}`;
                      navigator.clipboard.writeText(qrLink);
                      toast.success('🔗 Lien QR copié dans le presse-papiers !');
                    }}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    <QrCode className="h-4 w-4 mr-2" />
                    📋 Copier QR Code
                  </Button>
                </div>

                <Button
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Fermer
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceGeneratorFixed;